import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft,
  Printer,
  CreditCard,
  Banknote,
  CheckCircle,
  Clock,
  Truck,
  Phone,
  Mail,
  DollarSign,
  Building2,
  Send,
  MessageSquare,
  Pencil,
  X,
  Check,
  Edit2,
  Eye,
  Link as LinkIcon,
  Copy,
  Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SquarePaymentForm from "@/components/SquarePaymentForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";

const BASE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvoiceDetail() {
  const { id, businessSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  
  const API = `${BASE_API}/b/${businessSlug}`;
  const basePath = `/${businessSlug}`;
  
  const [invoice, setInvoice] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  // Preview modal — opens the print-friendly invoice in an iframe so the
  // user can verify what their customer is going to see WITHOUT leaving
  // the page or popping a new tab.
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  // Text-invoice dialog state. Mirrors the email dialog pattern.
  const [isTextDialogOpen, setIsTextDialogOpen] = useState(false);
  const [textPhone, setTextPhone] = useState("");
  const [sendingText, setSendingText] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [zelleInfo, setZelleInfo] = useState({ zelle_email: "", zelle_phone: "" });
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    payment_method: "zelle",
    notes: ""
  });
  
  // Tax edit state
  const [isEditingTax, setIsEditingTax] = useState(false);
  const [editTaxExempt, setEditTaxExempt] = useState(false);
  const [savingTax, setSavingTax] = useState(false);
  
  // Card fee edit state
  const [savingCardFee, setSavingCardFee] = useState(false);
  
  // Stripe payment loading state
  const [processingStripe, setProcessingStripe] = useState(false);
  
  // Square payment state
  const [processingSquare, setProcessingSquare] = useState(false);
  const [squareEnabled, setSquareEnabled] = useState(false);
  const [squareDevices, setSquareDevices] = useState([]);
  // Per-business line-item type labels so we render the rename ("Part" →
  // "Stock" / "Diagnostic" / custom keys) instead of the raw key on
  // every TYPE badge — buyers should never see "CUSTOM_2" on a printed
  // invoice.
  const [lineItemTypes, setLineItemTypes] = useState([]);

  // Resolve the displayable label for an item.type key. Falls back to
  // the key itself (capitalized) if the per-business config hasn't
  // loaded yet or the key is unknown.
  const labelForType = (key) => {
    const row = lineItemTypes.find((t) => t.key === key);
    if (row?.label) return row.label;
    if (!key) return "";
    return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
  };
  // Quick lookup: does this key behave like a Part (inventory-tracked)?
  // Drives badge color so custom inventory categories get the blue Part
  // styling and labor-style categories get purple.
  const isInventoryType = (key) => {
    const row = lineItemTypes.find((t) => t.key === key);
    if (row) return !!row.track_inventory;
    return key === "part";
  };
  const [isSquareDialogOpen, setIsSquareDialogOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState("");
  
  // Square Web Payment state
  const [isCardPaymentDialogOpen, setIsCardPaymentDialogOpen] = useState(false);
  const [squareConfig, setSquareConfig] = useState(null);
  const [activeProvider, setActiveProvider] = useState(null); // 'stripe' | 'square' | null
  const [processingCardPayment, setProcessingCardPayment] = useState(false);
  
  // Edit payment method state
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [editPaymentMethod, setEditPaymentMethod] = useState("");

  const fetchInvoice = useCallback(async () => {
    try {
      const [invoiceRes, paymentsRes, settingsRes, squareConfigRes, providerRes, lineTypesRes] = await Promise.all([
        axios.get(`${API}/invoices/${id}`),
        axios.get(`${API}/payments/invoice/${id}`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/payments/square-config`).catch(() => ({ data: { enabled: false } })),
        axios.get(`${API}/payments/active-provider`).catch(() => ({ data: { provider: null } })),
        axios.get(`${API}/line-item-types`).catch(() => ({ data: { line_item_types: [] } })),
      ]);
      setInvoice(invoiceRes.data);
      setPayments(paymentsRes.data);
      setPaymentForm(prev => ({ ...prev, amount: invoiceRes.data.total }));
      setEmailTo(invoiceRes.data.customer_email || "");
      setEditTaxExempt(invoiceRes.data.tax_exempt || false);
      setLineItemTypes(lineTypesRes.data?.line_item_types || []);
      if (settingsRes.data.zelle_info) {
        setZelleInfo(settingsRes.data.zelle_info);
      }
      // Set Square Web Payments config (used by the embedded card form
      // when the connected provider is Square)
      if (squareConfigRes.data.enabled) {
        setSquareConfig(squareConfigRes.data);
        setSquareEnabled(true);
      }
      // Whichever card-payment provider is actually connected for this
      // tenant — Stripe / Square / null. Drives which checkout flow the
      // "Pay with Card" button uses.
      setActiveProvider(providerRes.data?.provider || null);
      // Check if Square Terminal is enabled
      const squareSettings = settingsRes.data.payment_providers?.square;
      if (squareSettings?.enabled && squareSettings?.access_token) {
        setSquareEnabled(true);
        // Fetch Square devices
        try {
          const devicesRes = await axios.get(`${API}/square/devices`);
          setSquareDevices(devicesRes.data.devices || []);
        } catch (e) {
          console.log("Could not fetch Square devices");
        }
      }
    } catch (error) {
      toast.error("Failed to fetch invoice");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');
    const paidFlag = searchParams.get('paid');

    // Stripe checkout success → poll our new Connect-aware status
    // endpoint. Square legacy flow uses ?payment=success, the new
    // Stripe flow uses ?paid=1.
    if (sessionId && (paidFlag === '1' || paymentStatus === 'success')) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.info("Payment is being processed. Please refresh in a moment.");
      return;
    }

    try {
      // Try new Stripe Connect status route first, fall back to legacy
      // Square route for older sessions.
      let response;
      try {
        response = await axios.get(`${API}/payments/stripe/status/${sessionId}`);
      } catch (e) {
        response = await axios.get(`${API}/payments/status/${sessionId}`);
      }
      if (response.data.payment_status === 'paid') {
        toast.success("Payment successful!");
        fetchInvoice();
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
    }
  };

  const handleStripePayment = async () => {
    setProcessingStripe(true);
    try {
      // Hits the new Connect-aware endpoint that charges either via
      // the merchant's OAuth-linked stripe_user_id (preferred) or
      // their manually-pasted secret key, depending on how they
      // connected from the Payments page.
      const response = await axios.post(`${API}/payments/stripe/checkout`, {
        invoice_id: id,
        origin: window.location.origin,
      });
      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to initiate payment");
      setProcessingStripe(false);
    }
  };

  const handleManualPayment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/payments/manual`, {
        invoice_id: id,
        ...paymentForm
      });
      toast.success("Payment recorded successfully!");
      setIsPaymentDialogOpen(false);
      
      // Refresh invoice data
      fetchInvoice();
    } catch (error) {
      toast.error("Failed to record payment");
    }
  };

  const handleUpdatePaymentMethod = async (paymentId, newMethod) => {
    try {
      await axios.put(`${API}/payments/${paymentId}/method`, {
        payment_method: newMethod
      });
      toast.success("Payment method updated");
      setEditingPaymentId(null);
      fetchInvoice();
    } catch (error) {
      toast.error("Failed to update payment method");
    }
  };

  const handleSquarePayment = async () => {
    if (!selectedDevice) {
      toast.error("Please select a Square Terminal device");
      return;
    }
    
    setProcessingSquare(true);
    try {
      const amountCents = Math.round(invoice.total * 100);
      const response = await axios.post(`${API}/square/terminal-checkout`, null, {
        params: {
          invoice_id: id,
          device_id: selectedDevice,
          amount_cents: amountCents
        }
      });
      
      toast.success("Payment sent to Square Terminal! Complete payment on the device.", {
        duration: 5000
      });
      setIsSquareDialogOpen(false);
      
      // Poll for payment completion
      pollSquarePayment(response.data.checkout_id);
    } catch (error) {
      console.error("Square payment error:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate Square payment");
    } finally {
      setProcessingSquare(false);
    }
  };

  const pollSquarePayment = async (checkoutId, attempts = 0) => {
    const maxAttempts = 30; // Poll for up to 60 seconds
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      toast.info("Payment may still be processing. Please check the invoice status.");
      return;
    }
    
    // Check if invoice is now paid by refetching
    try {
      const invoiceRes = await axios.get(`${API}/invoices/${id}`);
      if (invoiceRes.data.payment_status === 'paid') {
        toast.success("Payment completed!");
        fetchInvoice(); // Refresh the invoice data
        setIsSquareDialogOpen(false);
        return;
      }
    } catch (e) {
      console.log("Error checking invoice status");
    }
    
    // Continue polling
    setTimeout(() => pollSquarePayment(checkoutId, attempts + 1), pollInterval);
  };

  const handleEmailInvoice = async () => {
    if (!emailTo) {
      toast.error("Please enter an email address");
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/email/invoice`, {
        invoice_id: id,
        recipient_email: emailTo
      });
      toast.success(`Invoice sent to ${emailTo}`);
      
      // Auto-save email to customer profile if different
      if (invoice.customer_id && emailTo !== invoice.customer_email) {
        try {
          await axios.put(`${API}/customers/${invoice.customer_id}`, {
            email: emailTo
          });
          toast.success("Customer email updated", { duration: 2000 });
          // Update local invoice data to reflect the change
          setInvoice(prev => ({ ...prev, customer_email: emailTo }));
        } catch (updateError) {
          // Silent fail - email was sent successfully, just couldn't update customer
          console.log("Could not update customer email:", updateError);
        }
      }
      
      setIsEmailDialogOpen(false);
    } catch (error) {
      toast.error("Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  // Send the invoice payment link via SMS. Mirrors the email handler — auto
  // saves the entered phone back to the customer profile if it's new/different.
  const handleTextInvoice = async () => {
    const phoneToSend = (textPhone || invoice.customer_phone || "").trim();
    if (!phoneToSend) {
      toast.error("Please enter a phone number");
      return;
    }
    setSendingText(true);
    try {
      const resp = await axios.post(
        `${API}/invoices/${id}/text`,
        { phone: phoneToSend, include_zelle: true }
      );
      const mocked = resp.data?.mocked;
      toast.success(
        mocked
          ? "Mock SMS sent (Twilio not connected — message recorded in log)"
          : `Text sent to ${resp.data?.to || phoneToSend}`,
      );
      // Auto-save phone to customer profile if different
      if (invoice.customer_id && phoneToSend !== invoice.customer_phone) {
        try {
          await axios.put(`${API}/customers/${invoice.customer_id}`, { phone: phoneToSend });
          setInvoice((prev) => ({ ...prev, customer_phone: phoneToSend }));
        } catch (updateError) {
          console.log("Could not update customer phone:", updateError);
        }
      }
      setIsTextDialogOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to send text");
    } finally {
      setSendingText(false);
    }
  };

  const handleSaveTaxStatus = async () => {
    setSavingTax(true);
    try {
      // Recalculate totals based on new tax exempt status
      const newTaxExempt = editTaxExempt;
      const taxRate = newTaxExempt ? 0 : invoice.tax_rate;
      
      await axios.put(`${API}/invoices/${id}`, {
        tax_exempt: newTaxExempt,
        tax_rate: taxRate
      });
      
      toast.success(newTaxExempt ? "Invoice marked as Tax Exempt" : "Tax applied to invoice");
      setIsEditingTax(false);
      fetchInvoice(); // Refresh to get updated totals
    } catch (error) {
      toast.error("Failed to update tax status");
    } finally {
      setSavingTax(false);
    }
  };

  const cancelTaxEdit = () => {
    setEditTaxExempt(invoice?.tax_exempt || false);
    setIsEditingTax(false);
  };

  const handleToggleCardFee = async () => {
    setSavingCardFee(true);
    try {
      const newCardFeeEnabled = !invoice.card_fee_enabled;
      
      await axios.put(`${API}/invoices/${id}`, {
        card_fee_enabled: newCardFeeEnabled
      });
      
      toast.success(newCardFeeEnabled ? "Card fee enabled" : "Card fee disabled");
      fetchInvoice(); // Refresh to get updated totals
    } catch (error) {
      toast.error("Failed to update card fee");
    } finally {
      setSavingCardFee(false);
    }
  };

  // Handle Square Web Payment
  const handleCardPayment = async (sourceId) => {
    setProcessingCardPayment(true);
    try {
      const balanceDue = invoice.total - (invoice.amount_paid || 0);
      const response = await axios.post(`${API}/payments/square-web`, {
        invoice_id: id,
        source_id: sourceId,
        amount: balanceDue
      });
      
      if (response.data.success) {
        toast.success("Payment successful!");
        setIsCardPaymentDialogOpen(false);
        fetchInvoice(); // Refresh invoice
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Payment failed";
      toast.error(errorMessage);
    } finally {
      setProcessingCardPayment(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    if (status === 'paid') {
      return (
        <Badge className="bg-green-100 text-green-700 border-green-200">
          <CheckCircle className="w-3 h-3 mr-1" />
          PAID
        </Badge>
      );
    } else if (status === 'partial') {
      return (
        <Badge className="bg-blue-100 text-blue-700 border-blue-200">
          PARTIAL
        </Badge>
      );
    }
    return (
      <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
        <Clock className="w-3 h-3 mr-1" />
        PENDING
      </Badge>
    );
  };

  const handlePrint = () => {
    navigate(`${basePath}/invoices/${id}/print`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="invoice-detail-loading">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>Invoice not found</p>
        <Link to={`${basePath}/invoices`}>
          <Button variant="link" className="text-blue-600 mt-2">
            Back to Invoices
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 animate-fade-in" data-testid="invoice-detail">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2 md:gap-3">
            <h1 className={`text-xl md:text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
              {invoice.invoice_number}
            </h1>
            {getStatusBadge(invoice.payment_status)}
            {invoice.tax_exempt && (
              <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                TAX EXEMPT
              </Badge>
            )}
          </div>
          <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            Created {new Date(invoice.created_at).toLocaleDateString()}
          </p>
        </div>
        
        {/* Action Buttons — split into "Share" (send the document) and
            "Get Paid" (collect money). Two visually distinct groups so the
            owner doesn't accidentally collect when they meant to send, and
            vice versa. */}
        <div className="space-y-3" data-testid="invoice-actions">
          {/* GROUP 1 · Share / send the document */}
          <div className={`p-3 rounded-lg border ${isDark ? 'bg-slate-800/40 border-slate-700' : 'bg-slate-50 border-slate-200'}`} data-testid="invoice-actions-share">
            <p className={`text-[10px] uppercase tracking-wider font-semibold mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Share document
            </p>
            <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => setIsPreviewOpen(true)}
                className={`text-sm ${isDark ? 'border-slate-700 hover:border-cyan-500' : 'border-slate-200 hover:border-cyan-500'}`}
                data-testid="preview-invoice-btn"
              >
                <Eye className="w-4 h-4 mr-1 md:mr-2" />
                Preview
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsEmailDialogOpen(true)}
                className={`text-sm ${isDark ? 'border-slate-700 hover:border-blue-500' : 'border-slate-200 hover:border-blue-500'}`}
                data-testid="email-invoice-btn"
              >
                <Send className="w-4 h-4 mr-1 md:mr-2" />
                Email
              </Button>
              <Button
                variant="outline"
                disabled
                className={`text-sm cursor-not-allowed opacity-60 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                data-testid="text-invoice-btn"
                title="Texting invoices is coming soon — we'll let you know."
              >
                <MessageSquare className="w-4 h-4 mr-1 md:mr-2" />
                Text
                <span className={`ml-1.5 text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full ${isDark ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
                  Soon
                </span>
              </Button>
              <Button
                variant="outline"
                onClick={handlePrint}
                className={`text-sm ${isDark ? 'border-slate-700 hover:border-blue-500' : 'border-slate-200 hover:border-blue-500'}`}
                data-testid="print-invoice-btn"
              >
                <Printer className="w-4 h-4 mr-1 md:mr-2" />
                Print
              </Button>
              {invoice.payment_status !== 'paid' && (
                <Link to={`${basePath}/invoices/${invoice.id}/edit`} className="contents">
                  <Button
                    variant="outline"
                    className={`text-sm w-full ${isDark ? 'border-slate-700 hover:border-yellow-500' : 'border-slate-200 hover:border-yellow-500'}`}
                    data-testid="edit-invoice-btn"
                  >
                    <Edit2 className="w-4 h-4 mr-1 md:mr-2" />
                    Edit
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* GROUP 2 · Get paid — only relevant while the invoice is unpaid */}
          {invoice.payment_status !== 'paid' && (
            <div className={`p-3 rounded-lg border ${isDark ? 'bg-emerald-500/5 border-emerald-700/40' : 'bg-emerald-50 border-emerald-200'}`} data-testid="invoice-actions-getpaid">
              <p className={`text-[10px] uppercase tracking-wider font-semibold mb-2 ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
                Get paid
              </p>
              <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2">
                <Button
                  onClick={() => setIsPaymentDialogOpen(true)}
                  className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="record-payment-btn"
                >
                  <Banknote className="w-4 h-4 mr-1 md:mr-2" />
                  Record Payment
                </Button>
                {activeProvider && (
                  <Button
                    onClick={() => {
                      // Route to whichever provider is actually
                      // connected. Stripe → hosted Checkout (handles
                      // Apple Pay / Google Pay). Square → embedded
                      // Web Payments dialog.
                      if (activeProvider === 'stripe') {
                        handleStripePayment();
                      } else if (activeProvider === 'square') {
                        setIsCardPaymentDialogOpen(true);
                      }
                    }}
                    disabled={processingCardPayment || processingStripe}
                    className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="pay-with-card-btn"
                  >
                    {(processingCardPayment || processingStripe) ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1 md:mr-2 animate-spin" />
                        Processing…
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-1 md:mr-2" />
                        Pay with Card
                      </>
                    )}
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => {
                    const link = `${window.location.origin}/${businessSlug}/pay/${invoice.id}`;
                    navigator.clipboard.writeText(link).then(
                      () => toast.success("Pay link copied to clipboard"),
                      () => toast.error("Could not copy link")
                    );
                  }}
                  className={`text-sm ${isDark ? 'border-emerald-700/60 text-emerald-300 hover:bg-emerald-500/10' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-100'}`}
                  data-testid="copy-pay-link-btn"
                >
                  <LinkIcon className="w-4 h-4 mr-1 md:mr-2" />
                  Copy pay link
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Invoice Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Info */}
          <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
            <CardHeader className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
              <CardTitle className={`text-lg ${isDark ? 'text-white' : 'text-slate-800'}`}>
                Bill To
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-2">
                <p className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  {invoice.customer_name}
                </p>
                {invoice.company_name && (
                  <div className={`flex items-center gap-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    <Building2 className="w-4 h-4" />
                    {invoice.company_name}
                  </div>
                )}
                {invoice.customer_phone && (
                  <div className={`flex items-center gap-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    <Phone className="w-4 h-4" />
                    {invoice.customer_phone}
                  </div>
                )}
                {invoice.customer_email && (
                  <div className={`flex items-center gap-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    <Mail className="w-4 h-4" />
                    {invoice.customer_email}
                  </div>
                )}
                {invoice.vehicle_info && (
                  <div className={`flex items-center gap-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    <Truck className="w-4 h-4" />
                    {invoice.vehicle_info}
                  </div>
                )}
                {invoice.truck_number && (
                  <div className={`flex items-center gap-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    <span className="font-medium">Truck #:</span> {invoice.truck_number}
                  </div>
                )}
                {invoice.driver_number && (
                  <div className={`flex items-center gap-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    <span className="font-medium">Driver #:</span> {invoice.driver_number}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
            <CardHeader className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
              <CardTitle className={`text-lg ${isDark ? 'text-white' : 'text-slate-800'}`}>
                Items
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {/* Table Header - Hidden on mobile */}
              <div className={`hidden md:grid grid-cols-12 gap-2 p-4 text-xs uppercase tracking-wider font-medium ${isDark ? 'bg-slate-700 text-slate-400' : 'bg-slate-50 text-slate-500'}`}>
                <div className="col-span-5">Description</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-1 text-center">Qty</div>
                <div className="col-span-2 text-right">Price</div>
                <div className="col-span-2 text-right">Total</div>
              </div>
              
              {/* Items */}
              <div className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-slate-200'}`}>
                {invoice.items.map((item, index) => (
                  <div key={index}>
                    {/* Desktop Layout */}
                    <div className="hidden md:grid grid-cols-12 gap-2 p-4 items-center">
                      <div className={`col-span-5 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                        {item.description}
                      </div>
                      <div className="col-span-2">
                        <Badge variant="outline" className={isInventoryType(item.type) ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200'}>
                          {labelForType(item.type).toUpperCase()}
                        </Badge>
                      </div>
                      <div className={`col-span-1 text-center ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                        {item.quantity}
                      </div>
                      <div className={`col-span-2 text-right ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                        {formatCurrency(item.unit_price)}
                      </div>
                      <div className={`col-span-2 text-right font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                        {formatCurrency(item.quantity * item.unit_price)}
                      </div>
                    </div>
                    
                    {/* Mobile Layout */}
                    <div className="md:hidden p-4 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className={`flex-1 font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                          {item.description || '(No description)'}
                        </div>
                        <Badge variant="outline" className={`text-xs ${isInventoryType(item.type) ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200'}`}>
                          {labelForType(item.type).toUpperCase()}
                        </Badge>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                          {item.quantity} × {formatCurrency(item.unit_price)}
                        </span>
                        <span className={`font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                          {formatCurrency(item.quantity * item.unit_price)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className={`p-4 space-y-2 ${isDark ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                <div className={`flex justify-between ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                  <span>Subtotal</span>
                  <span>{formatCurrency(invoice.subtotal)}</span>
                </div>
                
                {/* Editable Tax Row */}
                {isEditingTax ? (
                  <div className={`flex items-center justify-between py-2 px-3 rounded-lg ${isDark ? 'bg-slate-600' : 'bg-blue-50'}`}>
                    <div className="flex items-center gap-3">
                      <span className={isDark ? 'text-slate-200' : 'text-slate-700'}>Tax Exempt</span>
                      <Switch
                        checked={editTaxExempt}
                        onCheckedChange={setEditTaxExempt}
                        data-testid="tax-exempt-edit-switch"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={cancelTaxEdit}
                        className="h-7 w-7 p-0 hover:bg-red-100 hover:text-red-600"
                        disabled={savingTax}
                        data-testid="cancel-tax-edit-btn"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveTaxStatus}
                        className="h-7 bg-green-600 hover:bg-green-700 text-white px-3"
                        disabled={savingTax}
                        data-testid="save-tax-edit-btn"
                      >
                        {savingTax ? "..." : <Check className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className={`flex justify-between items-center ${(invoice.tax_exempt || invoice.tax_rate === 0 || invoice.tax_amount === 0) ? 'text-amber-600' : isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                    <div className="flex items-center gap-2">
                      <span>Tax {(invoice.tax_exempt || invoice.tax_rate === 0) ? '' : `(${(invoice.tax_rate * 100).toFixed(1).replace(/\.0$/, '')}%)`}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setIsEditingTax(true)}
                        className="h-6 w-6 p-0 hover:bg-blue-100 hover:text-blue-600"
                        data-testid="edit-tax-btn"
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                    </div>
                    <span>{(invoice.tax_exempt || invoice.tax_rate === 0 || invoice.tax_amount === 0) ? 'TAX EXEMPT' : formatCurrency(invoice.tax_amount)}</span>
                  </div>
                )}
                
                {/* Card Fee Row - with toggle for pending invoices */}
                {invoice.payment_status !== 'paid' ? (
                  <div className={`flex justify-between items-center ${invoice.card_fee_enabled ? 'text-blue-600' : isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    <div className="flex items-center gap-2">
                      <span>Card Fee (3%)</span>
                      <Switch
                        checked={invoice.card_fee_enabled || false}
                        onCheckedChange={handleToggleCardFee}
                        disabled={savingCardFee}
                        data-testid="card-fee-toggle"
                      />
                    </div>
                    <span>{invoice.card_fee_enabled ? formatCurrency(invoice.card_fee) : '$0.00'}</span>
                  </div>
                ) : (
                  invoice.card_fee_enabled && invoice.card_fee > 0 && (
                    <div className={`flex justify-between text-blue-600`}>
                      <span>Card Fee (3%)</span>
                      <span>{formatCurrency(invoice.card_fee)}</span>
                    </div>
                  )
                )}
                
                <Separator className={isDark ? 'bg-slate-600' : 'bg-slate-200'} />
                <div className="flex justify-between text-xl font-bold">
                  <span className={isDark ? 'text-white' : 'text-slate-800'}>Total</span>
                  <span className="text-blue-600">{formatCurrency(invoice.total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          {invoice.notes && (
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardHeader className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                <CardTitle className={`text-lg ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  Notes
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <p className={isDark ? 'text-slate-300' : 'text-slate-600'}>{invoice.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Payment Info */}
          <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
            <CardHeader className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
              <CardTitle className={`text-lg ${isDark ? 'text-white' : 'text-slate-800'}`}>
                Payment Info
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              {(zelleInfo.zelle_email || zelleInfo.zelle_phone) && (
                <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-blue-50'}`}>
                  <p className={`text-xs uppercase tracking-wider mb-2 ${isDark ? 'text-slate-400' : 'text-blue-600'}`}>
                    Zelle Payments
                  </p>
                  <div className="flex-1">
                    {zelleInfo.zelle_email && (
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                        {zelleInfo.zelle_email}
                      </p>
                    )}
                    {zelleInfo.zelle_phone && (
                      <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                        {zelleInfo.zelle_phone}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {invoice.due_date && (
                <div>
                  <p className={`text-xs uppercase tracking-wider mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    Due Date
                  </p>
                  <p className={isDark ? 'text-white' : 'text-slate-800'}>{invoice.due_date}</p>
                </div>
              )}

              {invoice.payment_method && (
                <div>
                  <p className={`text-xs uppercase tracking-wider mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    Payment Method
                  </p>
                  <p className={`capitalize ${isDark ? 'text-white' : 'text-slate-800'}`}>{invoice.payment_method}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment History */}
          {payments.length > 0 && (
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardHeader className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                <CardTitle className={`text-lg ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  Payment History
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-slate-200'}`}>
                  {payments.map((payment) => (
                    <div key={payment.id} className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                            {formatCurrency(payment.amount)}
                          </p>
                          {editingPaymentId === payment.id ? (
                            <div className="flex items-center gap-2 mt-1">
                              <Select value={editPaymentMethod} onValueChange={setEditPaymentMethod}>
                                <SelectTrigger className="h-7 w-24 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="cash">Cash</SelectItem>
                                  <SelectItem value="card">Card</SelectItem>
                                  <SelectItem value="zelle">Zelle</SelectItem>
                                  <SelectItem value="check">Check</SelectItem>
                                  <SelectItem value="square">Square</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button size="sm" className="h-6 px-2 text-xs" onClick={() => handleUpdatePaymentMethod(payment.id, editPaymentMethod)}>
                                Save
                              </Button>
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setEditingPaymentId(null)}>
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <p 
                              className={`text-xs capitalize cursor-pointer hover:underline ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                              onClick={() => {
                                setEditingPaymentId(payment.id);
                                setEditPaymentMethod(payment.payment_method);
                              }}
                              title="Click to edit payment method"
                            >
                              {payment.payment_method} ✎
                            </p>
                          )}
                        </div>
                        <Badge 
                          className={payment.payment_status === 'completed' 
                            ? "bg-green-100 text-green-700 border-green-200" 
                            : "bg-yellow-100 text-yellow-700 border-yellow-200"
                          }
                        >
                          {payment.payment_status}
                        </Badge>
                      </div>
                      <p className={`text-xs mt-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        {new Date(payment.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Preview Dialog — renders the print-friendly invoice view inside an
          iframe so the owner can verify what their customer will see without
          leaving the page. */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className={`max-w-4xl p-0 ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white'}`} data-testid="invoice-preview-dialog">
          <DialogHeader className={`px-6 pt-6 pb-3 border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
            <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
              <Eye className="w-5 h-5 text-cyan-500" />
              Invoice preview · {invoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          <div className="h-[70vh] bg-white overflow-hidden">
            <iframe
              key={isPreviewOpen ? "open" : "closed"}
              src={`${basePath}/invoices/${id}/print?preview=1`}
              title="Invoice preview"
              className="w-full h-full border-0"
              data-testid="invoice-preview-iframe"
            />
          </div>
          <DialogFooter className={`px-6 pb-6 pt-3 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
            <Button
              variant="outline"
              onClick={() => setIsPreviewOpen(false)}
              data-testid="preview-close-btn"
            >
              Close
            </Button>
            <Button
              onClick={handlePrint}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="preview-print-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Open print view
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Payment Dialog */}
      <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-slate-800'}>
              Record Payment
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleManualPayment} className="space-y-4">
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Amount</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="number"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) })}
                  className={`pl-10 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
                  required
                  data-testid="payment-amount-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Payment Method</Label>
              <Select 
                value={paymentForm.payment_method} 
                onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_method: value })}
              >
                <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'} data-testid="payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
                  <SelectItem value="zelle">Zelle</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="card">Card (Debit/Credit)</SelectItem>
                  <SelectItem value="cashapp">Cash App</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Notes (Optional)</Label>
              <Input
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                placeholder="Reference number, etc."
                data-testid="payment-notes-input"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsPaymentDialogOpen(false)}
                className={isDark ? 'border-slate-600' : 'border-slate-200'}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="submit-payment-btn"
              >
                Record Payment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Email Invoice Dialog */}
      <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-slate-800'}>
              Email Invoice
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Send To</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  className={`pl-10 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-slate-50 border-slate-200 text-slate-800'}`}
                  placeholder="customer@email.com"
                  data-testid="email-to-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEmailDialogOpen(false)}
                className={isDark ? 'border-slate-600' : 'border-slate-200'}
              >
                Cancel
              </Button>
              <Button
                onClick={handleEmailInvoice}
                disabled={sendingEmail || !emailTo}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="send-email-btn"
              >
                {sendingEmail ? "Sending..." : "Send Invoice"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Text Invoice Dialog — mirrors email pattern, mock-aware. */}
      <Dialog open={isTextDialogOpen} onOpenChange={setIsTextDialogOpen}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`} data-testid="text-invoice-dialog">
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-slate-800'}>
              Text Invoice
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              We'll send a tap-to-pay link via SMS. The customer can pay with card, Apple/Google Pay, or Zelle.
              The invoice flips to PAID automatically once they pay.
            </p>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Send To</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="tel"
                  value={textPhone}
                  onChange={(e) => setTextPhone(e.target.value)}
                  className={`pl-10 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-slate-50 border-slate-200 text-slate-800'}`}
                  placeholder="(508) 555-0149"
                  data-testid="text-phone-input"
                />
              </div>
              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                Standard SMS — the customer's carrier may charge them their normal rate. Texting STOP opts them out.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsTextDialogOpen(false)} className={isDark ? 'border-slate-600' : 'border-slate-200'}>
                Cancel
              </Button>
              <Button
                onClick={handleTextInvoice}
                disabled={sendingText || !textPhone}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="send-text-btn"
              >
                {sendingText ? "Sending..." : "Send Text"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Square Terminal Payment Dialog */}
      <Dialog open={isSquareDialogOpen} onOpenChange={setIsSquareDialogOpen}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-slate-800'}>
              Pay with Square Terminal
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-blue-50'}`}>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                {formatCurrency(invoice?.total || 0)}
              </p>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Amount to charge
              </p>
            </div>

            {squareDevices.length > 0 ? (
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                  Select Terminal Device
                </Label>
                <Select value={selectedDevice} onValueChange={setSelectedDevice}>
                  <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'}>
                    <SelectValue placeholder="Select a device" />
                  </SelectTrigger>
                  <SelectContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
                    {squareDevices.map((device) => (
                      <SelectItem key={device.id} value={device.id}>
                        {device.name || device.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className={`p-4 rounded-lg text-center ${isDark ? 'bg-yellow-900/20 border border-yellow-700' : 'bg-yellow-50 border border-yellow-200'}`}>
                <p className={`text-sm ${isDark ? 'text-yellow-400' : 'text-yellow-700'}`}>
                  No Square Terminal devices found. Make sure your device is paired in your Square Dashboard.
                </p>
              </div>
            )}

            <div className={`p-3 rounded text-sm ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
              <p>💳 The payment request will be sent to your Square Terminal.</p>
              <p className="mt-1">📱 Complete the payment on the device to finalize.</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsSquareDialogOpen(false)}
              className={isDark ? 'border-slate-600' : 'border-slate-200'}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSquarePayment}
              disabled={processingSquare || !selectedDevice || squareDevices.length === 0}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="send-to-terminal-btn"
            >
              {processingSquare ? "Sending..." : "Send to Terminal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Card Payment Dialog - Square Web Payments */}
      <Dialog open={isCardPaymentDialogOpen} onOpenChange={setIsCardPaymentDialogOpen}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-slate-800'}>
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-green-600" />
                Pay with Card
              </div>
            </DialogTitle>
            <DialogDescription className={isDark ? 'text-slate-400' : 'text-slate-500'}>
              Enter your card details to pay this invoice securely.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Amount Display */}
            <div className={`text-center p-4 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-slate-50'}`}>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Amount Due</p>
              <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                {formatCurrency((invoice?.total || 0) - (invoice?.amount_paid || 0))}
              </p>
            </div>

            {/* Square Payment Form */}
            {squareConfig?.application_id && squareConfig?.location_id ? (
              <SquarePaymentForm
                applicationId={squareConfig.application_id}
                locationId={squareConfig.location_id}
                environment={squareConfig.environment}
                amount={(invoice?.total || 0) - (invoice?.amount_paid || 0)}
                isDark={isDark}
                onPaymentSuccess={handleCardPayment}
                onPaymentError={(error) => toast.error(error)}
              />
            ) : (
              <div className={`text-center p-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Square payment not configured. Please check Settings.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
